import lottie, { AnimationItem } from 'lottie-web';
import { getReactProps } from '@m2h/common/utils/styleUtils';
import MachproHTMLElement from './common/machproHtmlElement';

type LottieCallback = (...args: any[]) => void;

interface LottieReactProps {
  animationData?: Record<string, any>;
  path?: string;
  loop?: boolean | string;
  autoplay?: boolean | string;
  resizeMode?: string;
  onDataLoaded?: LottieCallback;
  onAnimationFinish?: LottieCallback;
  onLoadError?: LottieCallback;
}

interface SegmentOptions {
  from: number;
  to: number;
}

interface PlayToOptions {
  to: number;
}

/**
 * MachPro <lottie> 的 Web Component 适配层。
 *
 * React 会把对象类型的 animationData 属性字符串化成 "[object Object]"，
 * 所以这里只把 attribute change 当作更新信号，真实值始终从 React props 读取。
 */
export default class Lottie extends MachproHTMLElement {
  private animation: AnimationItem | null = null;

  private initVersion = 0;

  private initTimer: number | null = null;

  private fetchController: AbortController | null = null;

  private isDisconnected = true;

  private animationUnsubscribers: Array<() => void> = [];

  private operationCompleteHandler: (() => void) | null = null;

  private operationPreviousLoop: boolean | number | null = null;

  private desiredPlayback: 'play' | 'pause' | 'stop' | null = null;

  connectedCallback() {
    super.connectedCallback();
    this.isDisconnected = false;
    this.style.display ||= 'block';
    this.style.overflow ||= 'hidden';
    this.scheduleInit();
  }

  disconnectedCallback() {
    this.isDisconnected = true;
    this.initVersion += 1;
    this.clearInitTimer();
    this.destroyCurrentAnimation();
    super.disconnectedCallback();
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (oldValue !== newValue && !this.isDisconnected) {
      this.scheduleInit();
    }
  }

  play(callback?: LottieCallback) {
    this.desiredPlayback = 'play';
    if (!this.animation) {
      callback?.(false);
      return;
    }
    this.animation.play();
    callback?.(true);
  }

  pause(callback?: LottieCallback) {
    this.desiredPlayback = 'pause';
    if (!this.animation) {
      callback?.(false);
      return;
    }
    this.animation.pause();
    callback?.(true);
  }

  stop(callback?: LottieCallback) {
    this.desiredPlayback = 'stop';
    if (!this.animation) {
      callback?.(false);
      return;
    }
    this.clearOperationCompleteHandler();
    this.animation.stop();
    callback?.(true);
  }

  playSegment(segment: SegmentOptions, callback?: LottieCallback) {
    if (!this.animation || !this.isValidFrame(segment?.from) || !this.isValidFrame(segment?.to)) {
      callback?.(false);
      return;
    }
    this.setOperationCompleteHandler(callback);
    this.animation.playSegments([segment.from, segment.to], true);
  }

  playTo(options: PlayToOptions, callback?: LottieCallback) {
    if (!this.animation || !this.isValidFrame(options?.to)) {
      callback?.(false);
      return;
    }
    this.setOperationCompleteHandler(callback);
    this.animation.playSegments([this.animation.currentFrame, options.to], true);
  }

  private scheduleInit() {
    this.clearInitTimer();
    this.initTimer = window.setTimeout(() => {
      this.initTimer = null;
      this.initialize();
    }, 0);
  }

  private clearInitTimer() {
    if (this.initTimer !== null) {
      window.clearTimeout(this.initTimer);
      this.initTimer = null;
    }
  }

  private async initialize() {
    if (this.isDisconnected) return;

    const props = (getReactProps(this) || {}) as LottieReactProps;
    const version = ++this.initVersion;
    this.destroyCurrentAnimation();

    try {
      let animationData = props.animationData ? this.cloneAnimationData(props.animationData) : undefined;
      if (!animationData && props.path) {
        const controller = typeof AbortController === 'undefined' ? null : new AbortController();
        this.fetchController = controller;
        const response = await fetch(props.path, controller ? { signal: controller.signal } : undefined);
        if (!response.ok) {
          throw new Error(`Lottie request failed: ${response.status} ${response.statusText}`);
        }
        animationData = this.prepareRemoteAnimationData(await response.json(), props.path);
      }

      if (this.isDisconnected || version !== this.initVersion) return;
      this.fetchController = null;
      if (!animationData) {
        throw new Error('Lottie requires animationData or path');
      }

      this.createAnimation(animationData, props);
    } catch (error) {
      if (this.isAbortError(error) || this.isDisconnected || version !== this.initVersion) return;
      this.fetchController = null;
      this.getLatestCallback('onLoadError')?.(error);
    }
  }

  private createAnimation(animationData: Record<string, any>, props: LottieReactProps) {
    let dataLoaded = false;
    let loadErrorNotified = false;
    const notifyDataLoaded = () => {
      if (dataLoaded) return;
      dataLoaded = true;
      this.fitRenderedContent();
      this.getLatestCallback('onDataLoaded')?.();
    };
    const notifyLoadError = (error?: any) => {
      if (loadErrorNotified) return;
      loadErrorNotified = true;
      this.getLatestCallback('onLoadError')?.(error);
    };

    try {
      const animation = lottie.loadAnimation({
        container: this,
        renderer: 'svg',
        loop: this.parseBoolean(props.loop, true),
        autoplay: this.parseBoolean(props.autoplay, true),
        animationData,
        rendererSettings: {
          preserveAspectRatio: this.getPreserveAspectRatio(props.resizeMode),
        },
      });
      this.animation = animation;
      this.listen(animation, 'DOMLoaded', () => this.fitRenderedContent());
      this.listen(animation, 'data_ready', notifyDataLoaded);
      this.listen(animation, 'data_failed', notifyLoadError);
      this.listen(animation, 'error', notifyLoadError);
      this.listen(animation, 'complete', () => this.getLatestCallback('onAnimationFinish')?.());
      this.applyDesiredPlayback();
    } catch (error) {
      notifyLoadError(error);
    }
  }

  private listen(animation: AnimationItem, eventName: any, handler: LottieCallback) {
    const unsubscribe = animation.addEventListener(eventName, handler);
    if (typeof unsubscribe === 'function') {
      this.animationUnsubscribers.push(unsubscribe);
    } else {
      this.animationUnsubscribers.push(() => animation.removeEventListener(eventName, handler));
    }
  }

  private setOperationCompleteHandler(callback?: LottieCallback) {
    this.clearOperationCompleteHandler();
    if (!this.animation) return;

    const animation = this.animation;
    this.operationPreviousLoop = animation.loop;
    animation.setLoop(false);
    const handler = () => {
      if (this.operationCompleteHandler !== handler) return;
      this.operationCompleteHandler = null;
      animation.removeEventListener('complete', handler);
      animation.setLoop(Boolean(this.operationPreviousLoop));
      this.operationPreviousLoop = null;
      callback?.(true);
    };
    this.operationCompleteHandler = handler;
    animation.addEventListener('complete', handler);
  }

  private clearOperationCompleteHandler() {
    if (this.animation && this.operationCompleteHandler) {
      this.animation.removeEventListener('complete', this.operationCompleteHandler);
      this.animation.setLoop(Boolean(this.operationPreviousLoop));
    }
    this.operationCompleteHandler = null;
    this.operationPreviousLoop = null;
  }

  private destroyCurrentAnimation() {
    this.fetchController?.abort();
    this.fetchController = null;
    this.clearOperationCompleteHandler();
    this.animationUnsubscribers.splice(0).forEach((unsubscribe) => unsubscribe());
    this.animation?.destroy();
    this.animation = null;
    while (this.firstChild) this.removeChild(this.firstChild);
  }

  private fitRenderedContent() {
    const renderedElement = this.firstElementChild as HTMLElement | null;
    if (!renderedElement) return;
    renderedElement.style.width = '100%';
    renderedElement.style.height = '100%';
    renderedElement.style.display = 'block';
  }

  private parseBoolean(value: boolean | string | undefined, fallback: boolean) {
    if (typeof value === 'boolean') return value;
    if (value === '' || value === 'true') return true;
    if (value === 'false') return false;
    return fallback;
  }

  private getPreserveAspectRatio(resizeMode?: string) {
    if (resizeMode === 'aspectFill') return 'xMidYMid slice';
    if (resizeMode === 'scaleToFill' || resizeMode === 'none') return 'none';
    return 'xMidYMid meet';
  }

  private cloneAnimationData(animationData: Record<string, any>) {
    return JSON.parse(JSON.stringify(animationData));
  }

  private prepareRemoteAnimationData(animationData: Record<string, any>, path: string) {
    const clonedData = this.cloneAnimationData(animationData);
    if (!Array.isArray(clonedData.assets)) return clonedData;

    // 通过 fetch 加载 JSON 后，lottie-web 不再知道 JSON 所在目录。
    // 将外链素材预先绝对化，避免 asset.u + asset.p 被错误解析到页面目录。
    clonedData.assets.forEach((asset: Record<string, any>) => {
      if (!asset || asset.e || typeof asset.p !== 'string' || asset.p.startsWith('data:')) return;
      try {
        asset.p = new URL(`${asset.u || ''}${asset.p}`, path).href;
        asset.u = '';
        asset.e = 1;
      } catch (error) {
        // 非法素材地址交给 lottie-web 的 error/data_failed 事件统一处理。
      }
    });
    return clonedData;
  }

  private getLatestCallback(name: 'onDataLoaded' | 'onAnimationFinish' | 'onLoadError') {
    return (getReactProps(this) as LottieReactProps | undefined)?.[name];
  }

  private applyDesiredPlayback() {
    if (!this.animation || !this.desiredPlayback) return;
    this.animation[this.desiredPlayback]();
  }

  private isValidFrame(value: any): value is number {
    return typeof value === 'number' && Number.isFinite(value);
  }

  private isAbortError(error: any) {
    return error?.name === 'AbortError';
  }

  static get observedAttributes() {
    return [
      ...super.observedAttributes,
      'animationdata',
      'path',
      'loop',
      'autoplay',
      'resizemode',
    ];
  }
}
